import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Loading,
  Modal,
  Table,
  type ColumnDefinition,
} from 'barte-design-system';

import { useProcessTerminalCsv } from '@/modules/backoffice/services/companies';

import styles from './csv-import-modal.module.css';
import { POSResultProcess } from '@/modules/backoffice/types/company';
import { getPOSStatusLabel } from '@/modules/backoffice/helpers/get-pos-status-label';
import { CsvRow, ResultRow, CsvImportModalProps } from './csv-import-modal.type';

const REQUIRED_HEADERS = ['serial_number', 'company_id', 'seller_id'];

const normalizeHeader = (value: string) => value.trim().toLowerCase();

const buildRowWithValidation = (row: Omit<CsvRow, 'hasError'>): CsvRow => {
  const hasError =
    !row.serialNumber.trim() ||
    (!row.companyId.trim() && !row.sellerId.trim());

  return {
    ...row,
    hasError,
  };
};

const parseCsvFile = async (
  file: File,
): Promise<{
  rows: CsvRow[];
  hasErrors: boolean;
}> => {
  const content = await file.text();
  const sanitized = content.replace(/^\uFEFF/, '').trim();

  if (!sanitized) {
    throw new Error('CSV_EMPTY');
  }

  const lines = sanitized
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length < 2) {
    throw new Error('CSV_HEADER_AND_ONE_ROW_REQUIRED');
  }

  const headers = lines[0].split(';').map(normalizeHeader);

  const missingHeaders = REQUIRED_HEADERS.filter(
    (header) => !headers.includes(header),
  );

  if (missingHeaders.length > 0) {
    throw new Error(`CSV_MISSING_HEADERS:${missingHeaders.join(', ')}`);
  }

  const serialIndex = headers.indexOf('serial_number');
  const companyIndex = headers.indexOf('company_id');
  const sellerIndex = headers.indexOf('seller_id');

  const rows = lines.slice(1).map((line, index) => {
    const columns = line.split(';').map((value) => value.trim());

    return buildRowWithValidation({
      id: index + 1,
      serialNumber: (columns[serialIndex] || '').toUpperCase(),
      companyId: columns[companyIndex] || '',
      sellerId: columns[sellerIndex] || '',
    });
  });

  return {
    rows,
    hasErrors: rows.some((row) => row.hasError),
  };
};

const buildTemplateCsv = () => {
  return [
    'serial_number;company_id;seller_id',
    'PB1S248P70602;1;',
    'PB1S248P70533;;2',
    'PB1S248S76189;1;3',
  ].join('\n');
};

const buildCsvFromRows = (rows: CsvRow[]) => {
  const header = 'serial_number;company_id;seller_id';

  const lines = rows.map(
    (row) =>
      `${row.serialNumber};${row.companyId || ''};${row.sellerId || ''}`,
  );

  return [header, ...lines].join('\n');
};

const mapResultsToRows = (
  success: POSResultProcess[],
  errors: POSResultProcess[],
): ResultRow[] => {
  return [
    ...success.map((item, index) => ({
      id: `success-${index}-${item.serialNumber}`,
      serialNumber: item.serialNumber,
      status: item.status,
      type: 'success' as const,
    })),
    ...errors.map((item, index) => ({
      id: `error-${index}-${item.serialNumber}`,
      serialNumber: item.serialNumber,
      status: item.status,
      type: 'error' as const,
    })),
  ];
};

export const CsvImportModal = ({
  isOpen,
  onClose,
}: CsvImportModalProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [step, setStep] = useState<'UPLOAD' | 'RESULT'>('UPLOAD');
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState<CsvRow[]>([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [hasValidationError, setHasValidationError] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [resultRows, setResultRows] = useState<ResultRow[]>([]);

  const { mutateAsync: processTerminalCsv, isPending } = useProcessTerminalCsv();

  const validationMessage = t(
    'backoffice.terminalsCsvValidationRowsMustHaveSerialAndOneId',
  );

  const handleReset = () => {
    setStep('UPLOAD');
    setFileName('');
    setRows([]);
    setErrorMessage('');
    setHasValidationError(false);
    setIsParsing(false);
    setResultRows([]);

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleOpenFilePicker = () => {
    inputRef.current?.click();
  };

  const handleRemoveSelectedFile = () => {
    setFileName('');
    setRows([]);
    setErrorMessage('');
    setHasValidationError(false);

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleDownloadTemplate = () => {
    const csvContent = buildTemplateCsv();
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;',
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = 'modelo-processamento-terminais.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  const refreshValidationState = (nextRows: CsvRow[]) => {
    const hasErrors = nextRows.some((row) => row.hasError);

    setRows(nextRows);
    setHasValidationError(hasErrors);

    if (hasErrors) {
      setErrorMessage(validationMessage);
    } else {
      setErrorMessage('');
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];

    if (!file) return;

    setIsParsing(true);
    setErrorMessage('');
    setRows([]);
    setHasValidationError(false);
    setFileName(file.name);

    try {
      const { rows: parsedRows, hasErrors } = await parseCsvFile(file);

      setRows(parsedRows);
      setHasValidationError(hasErrors);

      if (hasErrors) {
        setErrorMessage(validationMessage);
      }
    } catch (error: any) {
      const rawMessage = String(error?.message || '');

      if (rawMessage === 'CSV_EMPTY') {
        setErrorMessage(t('backoffice.terminalsCsvErrorEmpty'));
      } else if (rawMessage === 'CSV_HEADER_AND_ONE_ROW_REQUIRED') {
        setErrorMessage(t('backoffice.terminalsCsvErrorHeaderAndOneRow'));
      } else if (rawMessage.startsWith('CSV_MISSING_HEADERS:')) {
        const missingHeaders = rawMessage.replace('CSV_MISSING_HEADERS:', '');
        setErrorMessage(
          t('backoffice.terminalsCsvErrorMissingHeaders', {
            headers: missingHeaders,
          }),
        );
      } else {
        setErrorMessage(
          error?.message || t('backoffice.terminalsCsvErrorProcessFile'),
        );
      }
    } finally {
      setIsParsing(false);
    }
  };

  const handleRemoveRow = (rowId: number) => {
    const nextRows = rows
      .filter((row) => row.id !== rowId)
      .map((row, index) =>
        buildRowWithValidation({
          id: index + 1,
          serialNumber: row.serialNumber,
          companyId: row.companyId,
          sellerId: row.sellerId,
        }),
      );

    refreshValidationState(nextRows);
  };

  const handleSubmit = async () => {
    const csvContent = buildCsvFromRows(rows);
    const file = new File([csvContent], 'process-terminal.csv', {
      type: 'text/csv',
    });

    const response = await processTerminalCsv(file);

    if (!response.success || !response.data) {
      setErrorMessage(response.message);
      return;
    }

    const mapped = mapResultsToRows(
      response.data.success || [],
      response.data.errors || [],
    );

    setResultRows(mapped);
    setStep('RESULT');
  };

  const uploadColumns: ColumnDefinition<CsvRow>[] = useMemo(
    () => [
      {
        key: 'serialNumber',
        title: t('backoffice.terminalsCsvColumnSerialNumber'),
        dataIndex: 'serialNumber',
        render: (_, record) => (
          <span
            className={
              !record.serialNumber
                ? styles['cell-text-error']
                : styles['cell-text']
            }
          >
            {record.serialNumber || '-'}
          </span>
        ),
      },
      {
        key: 'companyId',
        title: t('backoffice.terminalsCsvColumnCompanyId'),
        dataIndex: 'companyId',
        render: (_, record) => {
          const invalid = !record.companyId && !record.sellerId;
          return (
            <span
              className={invalid ? styles['cell-text-error'] : styles['cell-text']}
            >
              {record.companyId || '-'}
            </span>
          );
        },
      },
      {
        key: 'sellerId',
        title: t('backoffice.terminalsCsvColumnSellerId'),
        dataIndex: 'sellerId',
        render: (_, record) => {
          const invalid = !record.companyId && !record.sellerId;
          return (
            <span
              className={invalid ? styles['cell-text-error'] : styles['cell-text']}
            >
              {record.sellerId || '-'}
            </span>
          );
        },
      },
      {
        key: 'actions',
        title: '',
        dataIndex: 'id',
        width: 60,
        render: (_, record) => (
          <button
            type="button"
            className={styles['delete-row-button']}
            onClick={() => handleRemoveRow(record.id)}
            aria-label={t('backoffice.terminalsCsvRemoveRowAria', {
              row: record.id,
            })}
            title={t('backoffice.terminalsCsvRemoveRowTitle')}
          >
            ×
          </button>
        ),
      },
    ],
    [rows, t],
  );

  const resultColumns: ColumnDefinition<ResultRow>[] = useMemo(
    () => [
      {
        key: 'serialNumber',
        title: t('backoffice.terminalsCsvColumnSerialNumber'),
        dataIndex: 'serialNumber',
      },
      {
        key: 'status',
        title: t('backoffice.terminalsCsvColumnStatus'),
        dataIndex: 'status',
        render: (_, record) => (
          <span
            className={
              record.type === 'success'
                ? styles['result-success']
                : styles['result-error']
            }
          >
            {getPOSStatusLabel(record.status, t)}
          </span>
        ),
      },
    ],
    [t],
  );

  const renderLoading = () => (
    <div className={styles['loading-container']}>
      <Loading
        description={t('backoffice.terminalsCsvProcessingLoading')}
        type="page"
      />
    </div>
  );

  const renderUpload = () => (
    <div className={styles.container}>
      <div className={styles['top-section']}>
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          onChange={handleFileChange}
          className={styles['hidden-input']}
        />

        <div className={styles.instructions}>
          <span className={styles['instructions-title']}>
            {t('backoffice.terminalsCsvExpectedStructureTitle')}
          </span>

          <code className={styles['instructions-code']}>
            serial_number;company_id;seller_id
          </code>

          <span className={styles['instructions-hint']}>
            {t('backoffice.terminalsCsvExpectedStructureHint')}
          </span>
        </div>

        <div className={styles['actions-row']}>
          <Button
            variant="outline"
            onClick={handleOpenFilePicker}
            disabled={isParsing || isPending}
          >
            {isParsing
              ? t('backoffice.terminalsCsvReadingFile')
              : t('backoffice.terminalsCsvSelectFile')}
          </Button>

          <Button
            variant="outline"
            onClick={handleDownloadTemplate}
            disabled={isParsing || isPending}
          >
            {t('backoffice.terminalsCsvDownloadTemplate')}
          </Button>

          {fileName && (
            <>
              <span className={styles['file-name']}>
                {t('backoffice.terminalsCsvFileLabel')} {fileName}
              </span>

              <button
                type="button"
                className={styles['remove-file-button']}
                onClick={handleRemoveSelectedFile}
              >
                {t('backoffice.terminalsCsvRemoveFile')}
              </button>
            </>
          )}
        </div>

        {errorMessage && (
          <div className={styles['error-box']}>
            {errorMessage}
          </div>
        )}
      </div>

      <div className={styles['table-wrapper']}>
        {rows.length > 0 ? (
          <div className={styles['table-scroll-area']}>
            <Table<CsvRow>
              columns={uploadColumns}
              data={rows}
              rowKey={(record) => record.id}
              size="sm"
            />
          </div>
        ) : (
          <div className={styles['empty-state']}>
            {t('backoffice.terminalsCsvEmptyState')}
          </div>
        )}
      </div>
    </div>
  );

  const renderResult = () => (
    <div className={styles['result-container']}>
      <div className={styles['result-summary']}>
        <span className={styles['result-summary-item']}>
          {t('backoffice.terminalsCsvResultsSuccessCount', {
            count: resultRows.filter((row) => row.type === 'success').length,
          })}
        </span>
        <span className={styles['result-summary-item']}>
          {t('backoffice.terminalsCsvResultsErrorCount', {
            count: resultRows.filter((row) => row.type === 'error').length,
          })}
        </span>
      </div>

      <div className={styles['table-wrapper']}>
        <div className={styles['table-scroll-area']}>
          <Table<ResultRow>
            columns={resultColumns}
            data={resultRows}
            rowKey={(record) => record.id}
            size="sm"
          />
        </div>
      </div>
    </div>
  );

  const getFooter = () => {
    if (step === 'UPLOAD') {
      return {
        buttonLayout: 'hug' as const,
        secondaryButton: {
          children: t('backoffice.terminalsCsvCancel'),
          variant: 'outline' as const,
          onClick: handleClose,
          disabled: isPending,
        },
        primaryButton: {
          children: isPending
            ? t('backoffice.terminalsCsvProcessingButton')
            : t('backoffice.terminalsCsvContinue'),
          onClick: handleSubmit,
          disabled:
            rows.length === 0 ||
            !!errorMessage ||
            isParsing ||
            hasValidationError ||
            isPending,
        },
      };
    }

    return {
      buttonLayout: 'hug' as const,
      primaryButton: {
        children: t('backoffice.terminalsCsvClose'),
        onClick: handleClose,
      },
    };
  };

  return (
    <Modal
      isOpen={isOpen}
      size="lg"
      title={
        step === 'UPLOAD'
          ? t('backoffice.terminalsCsvModalTitleUpload')
          : t('backoffice.terminalsCsvModalTitleResult')
      }
      onClose={handleClose}
      footer={getFooter()}
    >
      {isPending ? renderLoading() : step === 'UPLOAD' ? renderUpload() : renderResult()}
    </Modal>
  );
};